import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAssistantResponse } from '../services/aiService';
import { useCart } from '../context/CartContext';
import { useSnackbar } from 'notistack';
import api from '../config/api';

// ─── Context ────────────────────────────────────────────────────────────────
const VoiceAssistantContext = createContext(null);

export function VoiceAssistantProvider({ children }) {
  const navigate = useNavigate();

  return (
    <VoiceAssistantContext.Provider value={useVoiceAssistantEngine(navigate)}>
      {children}
    </VoiceAssistantContext.Provider>
  );
}

export function useVoiceAssistant() {
  const ctx = useContext(VoiceAssistantContext);
  if (!ctx) throw new Error('useVoiceAssistant must be used within a VoiceAssistantProvider');
  return ctx;
}

// Helper functions for navigation matching
function includesAny(text, values) {
  return values.some(value => text.includes(value));
}

function matchesNavigation(text, aliases) {
  const hasAlias = aliases.some(alias => text.includes(alias));
  if (!hasAlias) return false;

  // Short command (1-3 words) → match directly
  if (text.split(' ').length <= 3) return true;

  // Longer command → require explicit open intent
  const isOpenIntent = includesAny(text, [
    'abre', 'abrir', 'abreme', 'muestra', 'muestrame',
    'entra', 'entrar', 'llevame', 'navega', 'ver', 'mi', 'mis', 'ir a',
    'ir al', 'ir a los', 'ir a las', 'quiero ver', 'quiero ir'
  ]);
  return isOpenIntent;
}

// ─── Engine ──────────────────────────────────────────────────────────────────
function useVoiceAssistantEngine(navigate) {
  const { addProductToCart: addToCart } = useCart();
  const { enqueueSnackbar } = useSnackbar();

  const [isOpen, setIsOpen]             = useState(false);
  const [status, setStatus]             = useState('idle');
  const [transcript, setTranscript]     = useState('');
  const [response, setResponse]         = useState('');
  const [error, setError]               = useState('');
  const [permissionState, setPermState] = useState('unknown');
  const [mediaStream, setMediaStream]   = useState(null);
  const [pendingProduct, setPendingProductState] = useState(null);

  const [voiceConfig, setVoiceConfig] = useState({
    languageCode: 'es-ES',
    name: 'EXAVITQu4vr4xnSDxMaL' // Bella
  });
  const [audioConfig, setAudioConfig] = useState({
    speakingRate: 0.95,
    pitch: 1.0
  });

  // Load settings from localStorage
  useEffect(() => {
    try {
      const vc = window.localStorage.getItem('posent_voice_config');
      const ac = window.localStorage.getItem('posent_audio_config');
      if (vc) setVoiceConfig(JSON.parse(vc));
      if (ac) setAudioConfig(JSON.parse(ac));
    } catch (_) {}
  }, []);

  // Save settings to localStorage
  useEffect(() => {
    try {
      window.localStorage.setItem('posent_voice_config', JSON.stringify(voiceConfig));
      window.localStorage.setItem('posent_audio_config', JSON.stringify(audioConfig));
    } catch (_) {}
  }, [voiceConfig, audioConfig]);

  const pendingProductRef = useRef(null);
  const setPendingProduct = useCallback((p) => {
    pendingProductRef.current = p;
    setPendingProductState(p);
  }, []);

  // Refs to avoid stale closures inside recognition callbacks
  const recognitionRef   = useRef(null);
  const statusRef        = useRef('idle');
  const transcriptRef    = useRef('');
  const mediaStreamRef   = useRef(null);
  const startRecognitionRef = useRef(null);

  const supported = {
    stt: typeof window !== 'undefined' && Boolean(window.SpeechRecognition || window.webkitSpeechRecognition),
    tts: typeof window !== 'undefined' && 'speechSynthesis' in window,
  };

  useEffect(() => { statusRef.current = status; }, [status]);
  useEffect(() => { transcriptRef.current = transcript; }, [transcript]);
  useEffect(() => { mediaStreamRef.current = mediaStream; }, [mediaStream]);

  useEffect(() => {
    if (!isOpen) {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (_) {}
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(t => t.stop());
        setMediaStream(null);
      }
      if (statusRef.current === 'listening') {
        setStatus('idle');
        statusRef.current = 'idle';
      }
    }
  }, [isOpen, mediaStream]);

  const activeAudioRef = useRef(null);
  const lastSpokenTextRef = useRef('');
  const [isSpeaking, setIsSpeaking] = useState(false);

  // ── Speak ────────────────────────────────────────────────────────────────
  const speak = useCallback(async (text, onComplete) => {
    if (!text) {
      if (typeof onComplete === 'function') onComplete();
      return;
    }

    const fallbackBrowserTTS = (msg, cb) => {
      if (!supported.tts) {
        if (typeof cb === 'function') cb();
        return;
      }
      try {
        window.speechSynthesis.cancel();
        
        // Dividir en frases para inicio más rápido
        const sentences = msg
          .replace(/([.!?])\s+/g, '$1|||')
          .split('|||')
          .filter(s => s.trim().length > 0);

        let utteranceIndex = 0;
        const speakNext = () => {
          if (utteranceIndex >= sentences.length) {
            setIsSpeaking(false);
            setStatus('idle');
            statusRef.current = 'idle';
            if (typeof cb === 'function') cb();
            return;
          }

          const utter = new SpeechSynthesisUtterance(sentences[utteranceIndex]);
          utter.lang = voiceConfig.languageCode || 'es-ES';
          const voices = window.speechSynthesis.getVoices();
          const preferredVoice =
            voices.find(v =>
              new RegExp(`^${utter.lang.slice(0, 2)}[-_]`, 'i').test(v.lang) &&
              /google|microsoft|monica|paulina|sabina|lucia|female|male/i.test(v.name)
            ) ||
            voices.find(v => new RegExp(`^${utter.lang.slice(0, 2)}[-_]`, 'i').test(v.lang)) ||
            voices.find(v => /^es[-_]/i.test(v.lang));

          if (preferredVoice) utter.voice = preferredVoice;
          utter.rate = audioConfig.speakingRate;
          utter.pitch = audioConfig.pitch;
          utter.volume = 1.0;

          utter.onend = () => {
            utteranceIndex++;
            speakNext();
          };
          utter.onerror = (e) => {
            if (e.error !== 'interrupted' && e.error !== 'canceled') {
              console.warn('[PosentVoice] Browser TTS error:', e.error);
            }
            utteranceIndex++;
            speakNext();
          };
          window.speechSynthesis.speak(utter);
        };

        setTimeout(speakNext, 80);
      } catch (err) {
        console.warn('[PosentVoice] Fallback Browser TTS error:', err);
        setIsSpeaking(false);
        setStatus('idle');
        statusRef.current = 'idle';
        if (typeof cb === 'function') cb();
      }
    };

    try {
      // 1. Cancelar cualquier audio anterior
      window.speechSynthesis?.cancel();
      if (activeAudioRef.current) {
        try {
          activeAudioRef.current.pause();
          activeAudioRef.current.currentTime = 0;
        } catch (_) {}
        activeAudioRef.current = null;
      }
      setIsSpeaking(false);
      lastSpokenTextRef.current = text;

      // Detener el micrófono mientras habla
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (_) {}
      }

      setStatus('responding');
      statusRef.current = 'responding';
      setIsSpeaking(true);

      // 2. Intentar llamar al backend para ElevenLabs
      try {
        const ttsResponse = await api.post('/text-to-speech/convert', {
          text,
          voiceConfig,
          audioConfig
        });

        if (ttsResponse.data?.success && ttsResponse.data?.data?.audioContent) {
          const audio = new Audio(ttsResponse.data.data.audioContent);
          activeAudioRef.current = audio;
          audio.onended = () => {
            setIsSpeaking(false);
            setStatus('idle');
            statusRef.current = 'idle';
            activeAudioRef.current = null;
            if (typeof onComplete === 'function') onComplete();
          };
          audio.onerror = () => {
            console.warn('[PosentVoice] Error playing ElevenLabs audio, fallback to browser...');
            fallbackBrowserTTS(text, onComplete);
          };
          await audio.play();
          return;
        }

        fallbackBrowserTTS(text, onComplete);

      } catch (err) {
        console.warn('[PosentVoice] ElevenLabs API error, fallback to browser...', err);
        fallbackBrowserTTS(text, onComplete);
      }

    } catch (e) {
      console.warn('[PosentVoice] speak error:', e);
      setStatus('idle');
      statusRef.current = 'idle';
      setIsSpeaking(false);
      if (typeof onComplete === 'function') onComplete();
    }
  }, [supported.tts, voiceConfig, audioConfig]);

  const stopSpeaking = useCallback(() => {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    if (activeAudioRef.current) {
      try {
        activeAudioRef.current.pause();
        activeAudioRef.current.currentTime = 0;
      } catch (_) {}
      activeAudioRef.current = null;
    }
    setIsSpeaking(false);
    if (statusRef.current === 'responding') {
      setStatus('idle');
      statusRef.current = 'idle';
    }
  }, []);

  // ── Route map for Posent POS (matches App.jsx routes) ──────────────────────
  // ── Route map for Posent POS (matches App.jsx routes) ──────────────────────
  const ROUTE_MAP = {
    // Sales
    ventas: '/sales',
    venta: '/sales',
    cobrar: '/quick-sale',
    'nueva venta': '/quick-sale',
    'pos': '/quick-sale',
    'pdv': '/quick-sale',
    'punto de venta': '/quick-sale',
    'caja registradora': '/quick-sale',
    'caja': '/quick-sale',
    // Inventory
    inventario: '/inventory',
    productos: '/products',
    producto: '/products',
    stock: '/inventory',
    almacen: '/inventory',
    // Customers
    clientes: '/customers',
    cliente: '/customers',
    contactos: '/customers',
    // Repairs
    reparaciones: '/reparaciones',
    reparacion: '/reparaciones',
    taller: '/reparaciones',
    'servicio tecnico': '/reparaciones',
    arreglos: '/reparaciones',
    servicio: '/reparaciones',
    equipo: '/reparaciones',
    dispositivo: '/reparaciones',
    // Reports / Dashboard
    reportes: '/dashboard',
    estadisticas: '/analytics',
    'resumen': '/dashboard',
    inicio: '/dashboard',
    dashboard: '/dashboard',
    metricas: '/analytics',
    // Config
    configuracion: '/settings',
    ajustes: '/settings',
    configurar: '/settings',
    // Finance
    contabilidad: '/contabilidad',
    cuentas: '/contabilidad',
    balance: '/contabilidad',
    'estado financiero': '/contabilidad',
    gastos: '/contabilidad',
    finanzas: '/financial',
    proveedores: '/suppliers',
    proveedor: '/suppliers',
    facturacion: '/billing',
    factura: '/billing',
    recibo: '/billing',
    comprobante: '/billing',
    fiscal: '/billing',
    ncf: '/billing',
    'e-factura': '/billing',
    promociones: '/promotions',
  };

  // ── Run command ──────────────────────────────────────────────────────────
  const runCommand = useCallback(async (text, contextHint = {}) => {
    if (!text?.trim()) return;
    const clean = text.trim();
    setTranscript(clean);
    transcriptRef.current = clean;
    setStatus('processing');
    statusRef.current = 'processing';
    setError('');
    setResponse('');

    try {
      const normalized = clean.toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[¿?¡!.,;:]/g, ' ').replace(/\s+/g, ' ').trim();

      // A. Check if we have a pending product and user is confirming/cancelling
      if (pendingProductRef.current) {
        const isAffirmative = /^(si|si por favor|si claro|dale|agrega|agregar|confirmar|confirmo|yes|ok|okay|por favor|sisa)$/i.test(normalized) || 
                              normalized.includes('si') || normalized.includes('agrega') || normalized.includes('claro') || normalized.includes('dale');
        const isNegative = /^(no|no gracias|cancelar|cancela|ninguno|para|detener)$/i.test(normalized) ||
                            normalized.includes('no') || normalized.includes('cancela');

        if (isAffirmative) {
          const p = pendingProductRef.current;
          const productForCart = {
            id: p._id || p.id,
            name: p.nombre || p.name,
            price: parseFloat(p.precio || p.price || 0),
            quantity: 1,
            code: p.codigo || p.code || '',
            meta: { type: 'product', source: 'voice_assistant_confirm' }
          };
          addToCart(productForCart);
          enqueueSnackbar(`${p.nombre} agregado al carrito ✅`, { variant: 'success' });

          const addPool = [
            `He agregado ${p.nombre} al carrito.`,
            `Listo, ${p.nombre} ya está en tu carrito.`,
            `Perfecto, sumé ${p.nombre} a la venta actual.`
          ];
          const verbal = addPool[Math.floor(Math.random() * addPool.length)];
          setResponse(verbal);
          speak(verbal);

          setPendingProduct(null);
          setIsOpen(false);
          if (recognitionRef.current) {
            try { recognitionRef.current.stop(); } catch (_) {}
          }
          if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(t => t.stop());
            setMediaStream(null);
          }
          setStatus('idle');
          statusRef.current = 'idle';
          navigate('/quick-sale');
          return;
        } else if (isNegative) {
          const cancelPool = [
            'Entendido, operación cancelada.',
            'Olvídalo, no agregué nada.',
            'De acuerdo, cancelado.'
          ];
          const verbal = cancelPool[Math.floor(Math.random() * cancelPool.length)];
          setResponse(verbal);
          speak(verbal);
          setPendingProduct(null);
          setStatus('idle');
          statusRef.current = 'idle';
          return;
        }
      }

      // 1. Try navigation intent first (fast, local)
      let navigated = false;
      for (const [kw, route] of Object.entries(ROUTE_MAP)) {
        if (matchesNavigation(normalized, [kw])) {
          navigate(route);
          const navigationPool = [
            `Abriendo ${kw} de inmediato.`,
            `Entendido, yendo a la sección de ${kw}.`,
            `Listo, cargando ${kw}.`,
            `Claro, te llevo a ${kw} ahora.`
          ];
          const verbal = navigationPool[Math.floor(Math.random() * navigationPool.length)];
          setResponse(verbal);
          speak(verbal);
          navigated = true;

          // Close modal and stop listening/release mic tracks
          setIsOpen(false);
          if (recognitionRef.current) {
            try { recognitionRef.current.stop(); } catch (_) {}
          }
          if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(t => t.stop());
            setMediaStream(null);
          }
          setStatus('idle');
          statusRef.current = 'idle';
          break;
        }
      }
      if (navigated) return;

      // 2. Try product search (to add to cart)
      // Use plain strings instead of stateful regex /g to avoid .test() reset bugs
      const normalizedLow = normalized.toLowerCase();

      const isDirectAddIntent =
        /^(agrega|agregar|anadir|poner|llevar|compra|comprar|quiero|directo|pon)\s/i.test(normalizedLow) ||
        /(al\s+carrito|en\s+el\s+carrito|a\s+la\s+caja)\s*$/i.test(normalizedLow);

      const isSearchIntent =
        /^(busca|buscar|encuentra|encontrar|mostrar?|dame|tienes?)\s/i.test(normalizedLow);

      // Broader product detection: capture names, partial descriptions, codes
      const isQuestion = /^(cuanto|como|que|quien|donde|cuando|por\s+que|grafico|ventas\s+de|reporte|resumen|explica|ayuda)/i.test(normalizedLow);
      const isNavigationWord = /^(ir\s+a|abre|abrir|navega|lleva|entra|muestra\s+la\s+seccion)/i.test(normalizedLow);
      const wordCount = normalizedLow.trim().split(/\s+/).length;

      let isProductCommand = false;
      let isDirectAdd = false;

      if (isDirectAddIntent) {
        isProductCommand = true;
        isDirectAdd = true;
      } else if (isSearchIntent) {
        isProductCommand = true;
        isDirectAdd = false;
      } else if (!isQuestion && !isNavigationWord && wordCount <= 5) {
        // Short phrase that wasn't a question/nav command → likely a product name
        isProductCommand = true;
        isDirectAdd = false;
      }

      if (isProductCommand) {
        // Extract the product name: strip intent words and articles
        let searchWord = normalizedLow
          .replace(/^(agrega|agregar|anadir|poner|llevar|busca|buscar|encuentra|encontrar|comprar|quiero|dame|tienes|mostrar|pon|directo)\s+/i, '')
          .replace(/\s+(al\s+carrito|en\s+el\s+carrito|a\s+la\s+caja|al\s+pos|carrito)\s*$/i, '')
          .trim();

        // Strip leading articles and filler words iteratively
        let prev = '';
        while (searchWord !== prev) {
          prev = searchWord;
          searchWord = searchWord
            .replace(/^(el|la|los|las|un|una|producto|productos|de|para|uno|unos|unas)\s+/i, '')
            .trim();
        }

        if (searchWord.length >= 2) {
          try {
            const res = await api.get(`/products?search=${encodeURIComponent(searchWord)}`);
            if (res.data?.success && res.data.data?.length > 0) {
              const p = res.data.data[0];
              const productName = p.nombre || p.name || 'Producto';
              const price = p.precio || p.price || 0;

              if (isDirectAdd) {
                const productForCart = {
                  id: p._id || p.id,
                  name: productName,
                  price: parseFloat(price),
                  quantity: 1,
                  code: p.codigo || p.code || '',
                  meta: { type: 'product', source: 'voice_assistant_direct' }
                };
                addToCart(productForCart);
                enqueueSnackbar(`${productName} agregado al carrito ✅`, { variant: 'success' });

                const addPool = [
                  `He agregado ${productName} al carrito.`,
                  `Listo, ${productName} ya está en tu carrito.`,
                  `Perfecto, sumé ${productName} a la venta.`,
                  `Agregué ${productName}. ¿Algo más?`
                ];
                const verbal = addPool[Math.floor(Math.random() * addPool.length)];
                setResponse(verbal);
                speak(verbal);

                navigate('/quick-sale');
                setIsOpen(false);
                if (recognitionRef.current) {
                  try { recognitionRef.current.stop(); } catch (_) {}
                }
                if (mediaStreamRef.current) {
                  mediaStreamRef.current.getTracks().forEach(t => t.stop());
                  setMediaStream(null);
                }
                setStatus('idle');
                statusRef.current = 'idle';
                return;
              } else {
                // Show top matches if there are multiple
                const matches = res.data.data.slice(0, 3);
                if (matches.length > 1) {
                  // Multiple results: ask which one with product details
                  setPendingProduct(p); // default to first
                  const names = matches.map(m => `${m.nombre || m.name} (RD$ ${m.precio || m.price})`).join(', ');
                  const verbal = `Encontré ${matches.length} productos: ${names}. ¿Agrego ${productName} al carrito?`;
                  setResponse(verbal);
                  speak(verbal, () => {
                    if (pendingProductRef.current) {
                      setTimeout(() => {
                        if (startRecognitionRef.current) startRecognitionRef.current();
                      }, 350);
                    }
                  });
                } else {
                  // Single result: confirm directly
                  setPendingProduct(p);
                  const verbal = `Encontré ${productName} por RD$ ${price}. ¿Lo agrego al carrito?`;
                  setResponse(verbal);
                  speak(verbal, () => {
                    if (pendingProductRef.current) {
                      setTimeout(() => {
                        if (startRecognitionRef.current) startRecognitionRef.current();
                      }, 350);
                    }
                  });
                }
                return;
              }
            } else if (res.data?.success && res.data.data?.length === 0) {
              // Product not found — smart response
              const notFoundPool = [
                `No encontré "${searchWord}" en el inventario. ¿Intento con otro nombre?`,
                `"${searchWord}" no está en el catálogo. Puedes buscarlo manualmente en Productos.`,
                `No tengo resultados para "${searchWord}". Verifica el nombre o el código del producto.`
              ];
              const verbal = notFoundPool[Math.floor(Math.random() * notFoundPool.length)];
              setResponse(verbal);
              speak(verbal);
              setStatus('idle');
              statusRef.current = 'idle';
              return;
            }
          } catch (err) {
            console.error('[VoiceAssistant] Product search failed:', err);
            const verbal = 'Tuve un problema buscando el producto. Intenta de nuevo.';
            setResponse(verbal);
            speak(verbal);
            setStatus('idle');
            statusRef.current = 'idle';
            return;
          }
        }
      }

      // 3. AI fallback (OpenAI or local rule-based)
      const locationPath = contextHint.location || window.location.pathname;
      const userId = contextHint.userId;
      const userRole = contextHint.userRole;

      const aiResp = await getAssistantResponse(clean, locationPath, userRole, userId, []);
      const answerText = aiResp?.text || 'No encontré información para esa pregunta.';
      setResponse(answerText);
      speak(answerText);

    } catch (err) {
      console.error('[VoiceAssistant] runCommand error:', err);
      const msg = 'No pude procesar ese comando. Intenta de nuevo.';
      setError(msg);
      setStatus('error');
      statusRef.current = 'error';
      speak(msg);
    }
  }, [navigate, speak, addToCart, enqueueSnackbar, setPendingProduct]);

  // ── Start recognition ────────────────────────────────────────────────────
  const startRecognition = useCallback(() => {
    if (!supported.stt) {
      setError('Tu navegador no soporta reconocimiento de voz. Escribe el comando abajo.');
      setStatus('error');
      statusRef.current = 'error';
      return;
    }

    // Abort any existing session
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch (_) {}
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SpeechRecognition();
    rec.continuous      = false;
    rec.interimResults  = true;
    rec.lang            = 'es-US';
    rec.maxAlternatives = 3;

    let finalText = '';

    rec.onstart = () => {
      setStatus('listening');
      statusRef.current = 'listening';
      setError('');
      setTranscript('');
      transcriptRef.current = '';
      finalText = '';
    };

    rec.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalText += t;
        } else {
          interim += t;
        }
      }
      const display = finalText || interim;
      setTranscript(display);
      transcriptRef.current = display;
    };

    rec.onerror = (event) => {
      if (event.error === 'no-speech' || event.error === 'aborted') {
        if (statusRef.current === 'listening') {
          setStatus('idle');
          statusRef.current = 'idle';
        }
        return;
      }
      const denied = event.error === 'not-allowed' || event.error === 'service-not-allowed';
      setStatus('error');
      statusRef.current = 'error';
      if (denied) {
        setPermState('denied');
        setError('El micrófono está bloqueado.\n\n① Haz clic en el candado de la barra de direcciones\n② Busca "Micrófono" → Permitir\n③ Recarga la página');
      } else {
        setError(`Error de reconocimiento: ${event.error}. Intenta de nuevo.`);
      }
    };

    rec.onend = () => {
      // Use ref to get current value — avoids stale closure
      const captured = transcriptRef.current;
      const currentStatus = statusRef.current;

      // Liberar el micrófono inmediatamente al terminar de escuchar
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(t => t.stop());
        setMediaStream(null);
        mediaStreamRef.current = null;
      }

      if (captured && currentStatus !== 'error' && currentStatus !== 'processing' && currentStatus !== 'responding') {
        void runCommand(captured);
      } else if (currentStatus === 'listening') {
        setStatus('idle');
        statusRef.current = 'idle';
      }
    };

    recognitionRef.current = rec;
    try {
      rec.start();
      // Unlock iOS audio on first interaction
      if (supported.tts) {
        const unlock = new SpeechSynthesisUtterance('');
        unlock.volume = 0;
        window.speechSynthesis.speak(unlock);
      }
    } catch (err) {
      console.error('[VoiceAssistant] start error:', err);
      setError('No se pudo iniciar el micrófono.');
      setStatus('error');
      statusRef.current = 'error';
    }
  }, [supported.stt, supported.tts, runCommand]);

  useEffect(() => {
    startRecognitionRef.current = startRecognition;
  }, [startRecognition]);

  // ── Start listening (request mic → then start recognition) ───────────────
  const startListening = useCallback(() => {
    if (['listening', 'processing', 'responding'].includes(statusRef.current)) return;
    setError('');

    const doRequest = async () => {
      setStatus('processing');
      statusRef.current = 'processing';
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
        });
        setMediaStream(stream);
        setPermState('granted');
        startRecognition();
      } catch (err) {
        let stream2 = null;
        if (err.name === 'OverconstrainedError') {
          try { stream2 = await navigator.mediaDevices.getUserMedia({ audio: true }); } catch (_) {}
        }
        if (stream2) {
          setMediaStream(stream2);
          setPermState('granted');
          startRecognition();
          return;
        }
        setStatus('error');
        statusRef.current = 'error';
        if (err.name === 'NotAllowedError' || err.name === 'SecurityError') {
          setPermState('denied');
          setError('Permiso de micrófono denegado.\n\n① Haz clic en el candado de la barra de direcciones\n② Busca "Micrófono" → Permitir\n③ Recarga la página');
        } else if (err.name === 'NotFoundError') {
          setError('No se encontró ningún micrófono conectado.');
        } else if (err.name === 'NotReadableError') {
          setError('El micrófono está en uso por otra app. Ciérrala e intenta de nuevo.');
        } else {
          setError(err.message || 'No se pudo activar el micrófono.');
        }
      }
    };

    if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
      setStatus('error');
      statusRef.current = 'error';
      const isHttp = window.location.protocol !== 'https:' && !['localhost', '127.0.0.1'].includes(window.location.hostname);
      setError(isHttp ? 'El micrófono requiere HTTPS. Accede por https://' : 'Tu navegador no soporta acceso al micrófono.');
      return;
    }

    void doRequest();
  }, [startRecognition]);

  // ── Stop listening ───────────────────────────────────────────────────────
  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (_) {}
    }
    if (mediaStream) {
      mediaStream.getTracks().forEach(t => t.stop());
      setMediaStream(null);
    }
    if (statusRef.current === 'listening') {
      setStatus('idle');
      statusRef.current = 'idle';
    }
  }, [mediaStream]);

  // ── Open / Close ─────────────────────────────────────────────────────────
  const openAssistant = useCallback((options = {}) => {
    setIsOpen(true);
    setError('');
    setTranscript('');
    setResponse('');
    if (options.startListening) {
      setTimeout(() => startListening(), 200);
    }
  }, [startListening]);

  const closeAssistant = useCallback((keepSpeaking = false) => {
    setIsOpen(false);
    stopListening();
    if (!keepSpeaking) {
      window.speechSynthesis?.cancel();
    }
    setStatus('idle');
    statusRef.current = 'idle';
    setError('');
  }, [stopListening]);

  const confirmPendingProduct = useCallback(() => {
    if (!pendingProductRef.current) return;
    const p = pendingProductRef.current;
    const productForCart = {
      id: p._id || p.id,
      name: p.nombre || p.name,
      price: parseFloat(p.precio || p.price || 0),
      quantity: 1,
      code: p.codigo || p.code || '',
      meta: { type: 'product', source: 'voice_assistant_confirm_click' }
    };
    addToCart(productForCart);
    enqueueSnackbar(`${p.nombre} agregado al carrito ✅`, { variant: 'success' });
    const verbal = `He agregado ${p.nombre} al carrito.`;
    setResponse(verbal);
    speak(verbal);
    setPendingProduct(null);
    setIsOpen(false);
    navigate('/quick-sale');
  }, [addToCart, enqueueSnackbar, navigate, speak, setPendingProduct]);

  const cancelPendingProduct = useCallback(() => {
    setPendingProduct(null);
    setResponse('Operación cancelada.');
    speak('Entendido, no lo agregaré.');
  }, [speak, setPendingProduct]);

  // ── Retry ────────────────────────────────────────────────────────────────
  const retry = useCallback(() => {
    setError('');
    setStatus('idle');
    statusRef.current = 'idle';
    startListening();
  }, [startListening]);

  return {
    isOpen,
    status,
    transcript,
    response,
    error,
    permissionState,
    mediaStream,
    isProcessing: ['processing', 'listening'].includes(status),
    openAssistant,
    closeAssistant,
    startListening,
    stopListening,
    runCommand,
    retry,
    pendingProduct,
    confirmPendingProduct,
    cancelPendingProduct,
    voiceConfig,
    audioConfig,
    setVoiceConfig,
    setAudioConfig,
    isSpeaking,
    stopSpeaking,
  };
}
